import { useState, useEffect } from 'react';
import axios from 'axios';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Play, Users, CheckCircle, Clock, Trophy, Eye, UserCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

const LiveExamMode = ({ user }) => {
  const [sessions, setSessions] = useState([]);
  const [students, setStudents] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isMonitorDialogOpen, setIsMonitorDialogOpen] = useState(false);
  const [isResultsDialogOpen, setIsResultsDialogOpen] = useState(false);
  const [selectedSession, setSelectedSession] = useState(null);
  const [sessionResults, setSessionResults] = useState(null);
  const [pollingInterval, setPollingInterval] = useState(null);
  
  const [formData, setFormData] = useState({
    game_type: 'flash_number',
    selectedStudents: [],
    // Flash Number Config
    flashMode: 'sequential',
    flashLevel: 'medium',
    flashDigits: 4,
    flashSpeed: 1000,
    flashQuantity: 5,
    // Math Operations Config
    mathOperation: 'addition',
    mathDifficulty: 'easy',
    mathQuestions: 10
  });

  useEffect(() => {
    loadSessions();
    loadStudents();
    
    // Poll for session updates every 3 seconds
    const interval = setInterval(() => {
      loadSessions();
      if (selectedSession) {
        loadSessionDetails(selectedSession.id);
      }
    }, 3000);
    
    setPollingInterval(interval);
    
    return () => {
      if (interval) clearInterval(interval);
    };
  }, []);

  const loadSessions = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/exam-sessions`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSessions(response.data);
    } catch (error) {
      console.error('Error loading sessions:', error);
      toast.error('Failed to load exam sessions');
    }
  };

  const loadStudents = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/users?role=student&center_id=${user.center_id}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setStudents(response.data);
    } catch (error) {
      console.error('Error loading students:', error);
    }
  };

  const loadSessionDetails = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/api/exam-sessions/${sessionId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSelectedSession(response.data);
    } catch (error) {
      console.error('Error loading session details:', error);
    }
  };

  const handleStudentToggle = (studentId) => {
    setFormData(prev => ({
      ...prev,
      selectedStudents: prev.selectedStudents.includes(studentId)
        ? prev.selectedStudents.filter(id => id !== studentId)
        : [...prev.selectedStudents, studentId]
    }));
  };

  const handleCreateSession = async () => {
    if (formData.selectedStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      
      // Prepare game config based on game type
      let gameConfig = {};
      if (formData.game_type === 'flash_number') {
        gameConfig = {
          mode: formData.flashMode,
          level: formData.flashLevel,
          numberOfDigits: formData.flashDigits,
          flashSpeed: formData.flashSpeed,
          numberOfQuestions: formData.flashQuantity
        };
      } else if (formData.game_type === 'math_operations') {
        gameConfig = {
          operation: formData.mathOperation,
          difficulty: formData.mathDifficulty,
          numberOfQuestions: formData.mathQuestions
        };
      }
      
      await axios.post(
        `${API}/api/exam-sessions`,
        {
          game_type: formData.game_type,
          game_config: gameConfig,
          student_ids: formData.selectedStudents
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('Live exam session created! Waiting for students to be ready...');
      setIsCreateDialogOpen(false);
      setFormData({ 
        game_type: 'flash_number', 
        selectedStudents: [],
        flashMode: 'sequential',
        flashLevel: 'medium',
        flashDigits: 4,
        flashSpeed: 1000,
        flashQuantity: 5,
        mathOperation: 'addition',
        mathDifficulty: 'easy',
        mathQuestions: 10
      });
      loadSessions();
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error(error.response?.data?.detail || 'Failed to create exam session');
    }
  };

  const handleStartExam = async (sessionId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/api/exam-sessions/${sessionId}/start`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Exam started! Games are now launching for all students.');
      loadSessions();
      if (selectedSession) loadSessionDetails(sessionId);
    } catch (error) {
      console.error('Error starting exam:', error);
      toast.error(error.response?.data?.detail || 'Failed to start exam');
    }
  };

  const openMonitorDialog = (session) => {
    setSelectedSession(session);
    setIsMonitorDialogOpen(true);
  };

  const openResultsDialog = async (session) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${API}/api/exam-sessions/${session.id}/results`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSessionResults(response.data);
      setIsResultsDialogOpen(true);
    } catch (error) {
      console.error('Error loading results:', error);
      toast.error('Failed to load results');
    }
  };

  const handleDeleteSession = async (sessionId) => {
    if (!confirm('Are you sure you want to delete this exam session? This action cannot be undone.')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        `${API}/api/exam-sessions/${sessionId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Exam session deleted successfully');
      loadSessions();
    } catch (error) {
      console.error('Error deleting session:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete exam session');
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'created':
        return <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold">Created</span>;
      case 'waiting_ready':
        return <span className="px-3 py-1 bg-yellow-100 text-yellow-700 rounded-full text-xs font-semibold">All Ready</span>;
      case 'in_progress':
        return <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-semibold">In Progress</span>;
      case 'completed':
        return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">Completed</span>;
      default:
        return <span className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-xs font-semibold">{status}</span>;
    }
  };

  const canStartExam = (session) => {
    return session.status === 'waiting_ready' && 
           Object.values(session.student_ready_status || {}).every(ready => ready === true);
  };

  return (
    <div className="space-y-6">
      <Card className="p-6 shadow-xl border-0 bg-gradient-to-br from-indigo-50 via-white to-purple-50">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-xl shadow-lg">
              <Play className="w-8 h-8 text-white" />
            </div>
            <div>
              <h3 className="text-2xl font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                Live Exam Mode
              </h3>
              <p className="text-sm text-gray-600 mt-1">Conduct live exams with real-time student readiness tracking</p>
            </div>
          </div>
          <Button
            onClick={() => setIsCreateDialogOpen(true)}
            className="bg-gradient-to-r from-indigo-500 to-purple-600 hover:from-indigo-600 hover:to-purple-700 text-white shadow-lg"
          >
            <Play className="w-4 h-4 mr-2" />
            Create Live Exam
          </Button>
        </div>

        {/* Active Sessions */}
        <div className="space-y-4">
          {sessions.length === 0 ? (
            <div className="text-center py-12">
              <div className="inline-flex p-4 bg-gray-100 rounded-full mb-4">
                <Play className="w-12 h-12 text-gray-400" />
              </div>
              <p className="text-gray-600 text-lg font-medium">No live exam sessions yet</p>
              <p className="text-gray-500 text-sm mt-1">Create your first live exam to get started</p>
            </div>
          ) : (
            sessions.map(session => (
              <Card key={session.id} className="p-5 border-2 hover:shadow-lg transition-all bg-white">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h4 className="text-lg font-bold text-gray-900">
                        {session.game_type.replace('_', ' ').toUpperCase()} Game
                      </h4>
                      {getStatusBadge(session.status)}
                    </div>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-600 mt-2">
                      <span className="flex items-center gap-1">
                        <Users className="w-4 h-4" />
                        {session.student_ids.length} Students
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {new Date(session.created_at).toLocaleString()}
                      </span>
                    </div>

                    {/* Game Configuration Display */}
                    {session.game_config && (
                      <div className="mt-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                        <p className="text-xs text-gray-500 font-semibold mb-2">Game Configuration:</p>
                        <div className="flex flex-wrap gap-2 text-xs">
                          {session.game_type === 'flash_number' && (
                            <>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                Mode: {session.game_config.mode}
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                Level: {session.game_config.level}
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                {session.game_config.numberOfDigits} Digits
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                {session.game_config.flashSpeed}ms Speed
                              </span>
                              <span className="px-2 py-1 bg-purple-100 text-purple-700 rounded">
                                {session.game_config.numberOfQuestions} Questions
                              </span>
                            </>
                          )}
                          {session.game_type === 'math_operations' && (
                            <>
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                Operation: {session.game_config.operation}
                              </span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                Difficulty: {session.game_config.difficulty}
                              </span>
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded">
                                {session.game_config.numberOfQuestions} Questions
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    )}

                    {/* Ready Status */}
                    {(session.status === 'created' || session.status === 'waiting_ready') && (
                      <div className="mt-3 p-3 bg-yellow-50 rounded-lg border border-yellow-200">
                        <p className="text-xs text-yellow-700 font-semibold mb-2">Student Readiness:</p>
                        <div className="flex flex-wrap gap-2">
                          {Object.entries(session.student_ready_status || {}).map(([studentId, isReady]) => (
                            <div key={studentId} className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs ${
                              isReady ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'
                            }`}>
                              {isReady ? <CheckCircle className="w-3 h-3" /> : <Clock className="w-3 h-3" />}
                              {session.student_names[studentId]}
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Results Summary */}
                    {session.status === 'completed' && (
                      <div className="mt-3 flex items-center gap-2 text-sm">
                        <Trophy className="w-4 h-4 text-yellow-500" />
                        <span className="text-gray-700">
                          {Object.keys(session.results || {}).length}/{session.student_ids.length} students completed
                        </span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    {canStartExam(session) && (
                      <Button
                        onClick={() => handleStartExam(session.id)}
                        className="bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white shadow-lg"
                        size="sm"
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Start Exam
                      </Button>
                    )}
                    
                    {(session.status === 'created' || session.status === 'waiting_ready') && (
                      <Button
                        onClick={() => openMonitorDialog(session)}
                        variant="outline"
                        size="sm"
                      >
                        <UserCheck className="w-4 h-4 mr-2" />
                        Monitor
                      </Button>
                    )}
                    
                    {session.status === 'completed' && (
                      <Button
                        onClick={() => openResultsDialog(session)}
                        className="bg-blue-500 hover:bg-blue-600 text-white"
                        size="sm"
                      >
                        <Eye className="w-4 h-4 mr-2" />
                        View Results
                      </Button>
                    )}

                    {/* Delete Button - Always visible for any status */}
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteSession(session.id);
                      }}
                      variant="outline"
                      size="sm"
                      className="border-red-300 text-red-600 hover:bg-red-50 hover:border-red-400"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </Card>
            ))
          )}
        </div>
      </Card>

      {/* Create Session Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Create Live Exam Session</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="game_type">Game Type</Label>
              <Select
                value={formData.game_type}
                onValueChange={(value) => setFormData({ ...formData, game_type: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="flash_number">Flash Number Game</SelectItem>
                  <SelectItem value="math_operations">Math Operations</SelectItem>
                  <SelectItem value="memory_game">Memory Game</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Flash Number Game Configuration */}
            {formData.game_type === 'flash_number' && (
              <div className="space-y-4 p-4 bg-purple-50 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-purple-900">Flash Number Configuration</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Mode</Label>
                    <Select value={formData.flashMode} onValueChange={(value) => setFormData({ ...formData, flashMode: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="sequential">Sequential</SelectItem>
                        <SelectItem value="random">Random</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Level</Label>
                    <Select value={formData.flashLevel} onValueChange={(value) => setFormData({ ...formData, flashLevel: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Number of Digits</Label>
                    <Select value={formData.flashDigits.toString()} onValueChange={(value) => setFormData({ ...formData, flashDigits: parseInt(value) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="2">2 Digits</SelectItem>
                        <SelectItem value="3">3 Digits</SelectItem>
                        <SelectItem value="4">4 Digits</SelectItem>
                        <SelectItem value="5">5 Digits</SelectItem>
                        <SelectItem value="6">6 Digits</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Flash Speed (ms)</Label>
                    <Select value={formData.flashSpeed.toString()} onValueChange={(value) => setFormData({ ...formData, flashSpeed: parseInt(value) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="500">Fast (500ms)</SelectItem>
                        <SelectItem value="1000">Medium (1s)</SelectItem>
                        <SelectItem value="1500">Slow (1.5s)</SelectItem>
                        <SelectItem value="2000">Very Slow (2s)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Number of Questions</Label>
                    <Select value={formData.flashQuantity.toString()} onValueChange={(value) => setFormData({ ...formData, flashQuantity: parseInt(value) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="3">3 Questions</SelectItem>
                        <SelectItem value="5">5 Questions</SelectItem>
                        <SelectItem value="10">10 Questions</SelectItem>
                        <SelectItem value="15">15 Questions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            {/* Math Operations Configuration */}
            {formData.game_type === 'math_operations' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
                <h4 className="font-semibold text-blue-900">Math Operations Configuration</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Operation</Label>
                    <Select value={formData.mathOperation} onValueChange={(value) => setFormData({ ...formData, mathOperation: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="addition">Addition</SelectItem>
                        <SelectItem value="multiplication">Multiplication</SelectItem>
                        <SelectItem value="mixed">Mixed</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Difficulty</Label>
                    <Select value={formData.mathDifficulty} onValueChange={(value) => setFormData({ ...formData, mathDifficulty: value })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="easy">Easy</SelectItem>
                        <SelectItem value="medium">Medium</SelectItem>
                        <SelectItem value="hard">Hard</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label>Number of Questions</Label>
                    <Select value={formData.mathQuestions.toString()} onValueChange={(value) => setFormData({ ...formData, mathQuestions: parseInt(value) })}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="5">5 Questions</SelectItem>
                        <SelectItem value="10">10 Questions</SelectItem>
                        <SelectItem value="15">15 Questions</SelectItem>
                        <SelectItem value="20">20 Questions</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
            )}

            <div>
              <Label>Select Students (All must click "Ready" before you can start)</Label>
              <div className="mt-2 max-h-60 overflow-y-auto border rounded-lg p-4 space-y-2">
                {students.map(student => (
                  <div key={student.id} className="flex items-center space-x-2">
                    <Checkbox
                      id={student.id}
                      checked={formData.selectedStudents.includes(student.id)}
                      onCheckedChange={() => handleStudentToggle(student.id)}
                    />
                    <Label htmlFor={student.id} className="cursor-pointer">
                      {student.name}
                    </Label>
                  </div>
                ))}
              </div>
            </div>

            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <p className="text-sm text-blue-900">
                <strong>How it works:</strong>
              </p>
              <ol className="text-sm text-blue-800 mt-2 space-y-1 ml-4">
                <li>1. Students will see this exam notification on their dashboard</li>
                <li>2. Each student must click "I'm Ready" button</li>
                <li>3. Once all students are ready, click "Start Exam"</li>
                <li>4. Numbers will start flashing IMMEDIATELY on all student screens</li>
                <li>5. Students complete the game and results are auto-submitted</li>
              </ol>
            </div>

            <Button onClick={handleCreateSession} className="w-full bg-indigo-600 hover:bg-indigo-700">
              Create Session
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Monitor Dialog */}
      <Dialog open={isMonitorDialogOpen} onOpenChange={setIsMonitorDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Monitor Exam Session</DialogTitle>
          </DialogHeader>
          {selectedSession && (
            <div className="space-y-4">
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-sm text-gray-600 mb-2">
                  <strong>Game:</strong> {selectedSession.game_type.replace('_', ' ').toUpperCase()}
                </p>
                <p className="text-sm text-gray-600">
                  <strong>Status:</strong> {selectedSession.status}
                </p>
              </div>

              <div>
                <Label className="text-base font-semibold mb-3 block">Student Readiness Status:</Label>
                <div className="space-y-2">
                  {Object.entries(selectedSession.student_ready_status || {}).map(([studentId, isReady]) => (
                    <div key={studentId} className={`p-3 rounded-lg border-2 flex items-center justify-between ${
                      isReady ? 'bg-green-50 border-green-200' : 'bg-gray-50 border-gray-200'
                    }`}>
                      <span className="font-medium">{selectedSession.student_names[studentId]}</span>
                      {isReady ? (
                        <span className="flex items-center gap-1 text-green-700 font-semibold">
                          <CheckCircle className="w-5 h-5" />
                          Ready
                        </span>
                      ) : (
                        <span className="flex items-center gap-1 text-gray-500">
                          <Clock className="w-5 h-5" />
                          Waiting...
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {canStartExam(selectedSession) && (
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <p className="text-green-800 font-semibold mb-3">All students are ready! Numbers will start flashing immediately on their screens.</p>
                  <Button
                    onClick={() => {
                      handleStartExam(selectedSession.id);
                      setIsMonitorDialogOpen(false);
                    }}
                    className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 text-white"
                  >
                    <Play className="w-4 h-4 mr-2" />
                    Start Exam Now
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Results Dialog */}
      <Dialog open={isResultsDialogOpen} onOpenChange={setIsResultsDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Exam Results</DialogTitle>
          </DialogHeader>
          {sessionResults && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <Card className="p-4 bg-blue-50 border-blue-200">
                  <p className="text-sm text-blue-600 font-semibold">Total Students</p>
                  <p className="text-2xl font-bold text-blue-900">{sessionResults.total_students}</p>
                </Card>
                <Card className="p-4 bg-green-50 border-green-200">
                  <p className="text-sm text-green-600 font-semibold">Submitted</p>
                  <p className="text-2xl font-bold text-green-900">{sessionResults.submitted_count}</p>
                </Card>
                <Card className="p-4 bg-yellow-50 border-yellow-200">
                  <p className="text-sm text-yellow-600 font-semibold">Pending</p>
                  <p className="text-2xl font-bold text-yellow-900">
                    {sessionResults.pending_students?.length || 0}
                  </p>
                </Card>
              </div>

              {/* Results Table */}
              <div className="border rounded-lg overflow-hidden">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Student Name</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Score</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Time Taken</th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {sessionResults.results?.map((result) => (
                      <tr key={result.student_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{result.student_name}</td>
                        <td className="px-4 py-3">
                          <span className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm font-semibold">
                            {result.result.score}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-gray-600">
                          {result.result.time_taken ? `${result.result.time_taken}s` : 'N/A'}
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-green-600">
                            <CheckCircle className="w-4 h-4" />
                            Completed
                          </span>
                        </td>
                      </tr>
                    ))}
                    {sessionResults.pending_students?.map((student) => (
                      <tr key={student.student_id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-medium text-gray-900">{student.student_name}</td>
                        <td className="px-4 py-3 text-gray-400">-</td>
                        <td className="px-4 py-3 text-gray-400">-</td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-yellow-600">
                            <Clock className="w-4 h-4" />
                            Pending
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LiveExamMode;
